import React, { useState, useEffect } from 'react';


import firebaseApp from "./database";

import PatientsService from '../services/patientsService';
import Popup from "./popup";
import TermsPanel from "./termsPanel";
import AccountVerificationPanel from "./accountVerificationPanel";
import { trimPhoneNumber } from '../utils';
import LoggingService from '../services/loggingService';
import { LogItemType } from '../models/logItem';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { createUserWithEmailAndPassword, getAuth, linkWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { MuiTelInput } from 'mui-tel-input';
import { t } from 'i18next';
import { Trans } from 'react-i18next';

const functions = getFunctions(firebaseApp, 'europe-west3');

export default function RegisterUserPanel(props) {

    const [mobilePhoneNumber, setMobilePhoneNumber] = useState("");
    const [gender, setGender] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [acceptTerms, setAcceptTerms] = useState(false);

    const [recaptchaSolved, setRecaptchaSolved] = useState(false);
    const [smsHasBeenSent, setSmsHasBeenSent] = useState(false);

    const [termsPopupVisible, setTermsPopupVisible] = useState(false);

    useEffect(() => {

        setSmsHasBeenSent(false);


        // Turn off phone auth app verification for testing        
        getAuth().settings.appVerificationDisabledForTesting = false;

        try {
            // init recaptcha verifier
            (window as any).recaptchaVerifier = new RecaptchaVerifier(getAuth(), "recaptcha-container-register", {
                'size': 'normal',
                'callback': function (response) {
                    // reCAPTCHA solved, allow register.
                    console.log("recaptcha solved");
                    setRecaptchaSolved(true);
                },
                'expired-callback': function () {
                    console.log("recaptcha expired");
                    setRecaptchaSolved(false);
                }
            });

        } catch (error) {
            console.error(error);
        }

        (window as any).recaptchaVerifier.render().then(function (widgetId) {
            (window as any).recaptchaWidgetId = widgetId;
        }).catch((error) => {
            console.error(error);
        });


    }, []);


    // creates an email user
    // sends a SMS verification code
    // signs the new created email user out again
    function onRegisterFormSubmit(event) {

        if (event) {
            event.preventDefault();
        }

        // create an email user
        createUserWithEmailAndPassword(getAuth(), email, password)
            .then((result: any) => {

                if (!result && !result.user && !result.user.uid) return;

                const setPatientClaims = httpsCallable(functions, "setPatientClaims");
                setPatientClaims({ uid: result.user.uid });

                let phoneNumber = trimPhoneNumber(mobilePhoneNumber);

                // link the email auth account with the phone number auth account, otherwise firebase creates two different accounts
                const currentUser = getAuth().currentUser;
                if (!currentUser) throw (t("components.registerUserPanel.couldNotFindUser"));

                linkWithPhoneNumber(currentUser, phoneNumber, (window as any).recaptchaVerifier)
                    .then((confirmationResult) => {

                        (window as any).confirmationResult = confirmationResult;

                        getAuth().signOut();

                        setSmsHasBeenSent(true);

                        console.log("activation SMS has been sent");

                    })
                    .catch((error) => {
                        // Error occurred.
                        console.log(error);
                        getAuth().signOut();
                    });


                const patient = {
                    id: result.user.uid,
                    isOnlineUser: true,
                    firstName: firstName,
                    lastName: lastName,
                    gender: gender,
                    email: email,
                    mobilePhoneNumber: mobilePhoneNumber,
                    marketingAllowed: true,
                    reminderAllowed: true,
                    smsAllowed: true,
                    emailAllowed: true,
                    newPatient: true,
                    createdAt: new Date()
                };
                PatientsService.createPatient(patient)
                    .then(() => {
                        //LoggingService.log(LogItemType.recallLandingPageLoaded, "", `New user registered ${patient.firstName} ${patient.lastName}`, "", "");
                    })
                    .catch((error) => {

                        const errorCode = error.code;

                        switch (errorCode) {

                            case "auth/invalid-phone-number":
                                alert(t("components.registerUserPanel.invalidPhoneNumber"));
                                break;

                            default:
                                alert(error.message);
                                break;
                        }


                        getAuth().signOut();
                    });

            })
            .catch((error) => {

                const errorCode = error.code;

                switch (errorCode) {
                    case "auth/email-already-in-use":
                    case "auth/email-already-exists":
                        alert(t("components.registerUserPanel.emailExists"));
                        break;

                    case "auth/invalid-email":
                        alert(t("components.registerUserPanel.emailInvalid"));
                        break;

                    case "auth/invalid-password":
                    case "auth/weak-password":
                        alert(t("components.registerUserPanel.weakPassword"));
                        break;

                    case "auth/invalid-phone-number":
                        alert(t("components.registerUserPanel.phoneNumberInvalid"));
                        break;

                    default:
                        alert(error.message);
                        break;
                }
            });

    }


    function onShowTermsClick() {
        setTermsPopupVisible(true);
    }

    function handleCloseTermsPopup() {
        setTermsPopupVisible(false);
    }


    return (
        smsHasBeenSent ?
            <AccountVerificationPanel
                _smsHasBeenSent={smsHasBeenSent}
                _email={email}
                _password={password}
                _mobilePhoneNumber={mobilePhoneNumber}
            />
            :
            <div className="kt-panel">
                <div className="kt-panel-content">
                    <div>
                        <div className="kt-center">{t("components.registerUserPanel.newAtPickadoc")}</div>
                        <form name="register" onSubmit={onRegisterFormSubmit}>

                            <input name="email" type="email" placeholder={t("components.registerUserPanel.email")} required value={email} onChange={e => setEmail(e.target.value)} />
                            <input name="password" type="password" placeholder={t("components.registerUserPanel.password")} required value={password} onChange={e => setPassword(e.target.value)} />

                            <div>
                                <label>{t("components.registerUserPanel.mrs")} <input type="radio" name="gender" value="f" required checked={gender === "f"} onChange={e => setGender(e.target.value)} /></label>
                                <label>{t("components.registerUserPanel.mr")} <input type="radio" name="gender" value="m" checked={gender === "m"} onChange={e => setGender(e.target.value)} /></label>
                            </div>

                            <input name="firstname" type="text" placeholder={t("components.registerUserPanel.firstName")} required value={firstName} onChange={e => setFirstName(e.target.value)} />
                            <input name="lastname" type="text" placeholder={t("components.registerUserPanel.lastName")} required value={lastName} onChange={e => setLastName(e.target.value)} />


                            <MuiTelInput
                                value={mobilePhoneNumber}
                                onChange={newValue => setMobilePhoneNumber(newValue)}
                                required
                                name="phone"
                                placeholder={` ${t("components.registerUserPanel.mobilePhoneNumber")}`}
                                defaultCountry='DE'

                            />
                            <div className="kt-explanation kt-center">
                                <Trans i18nKey="components.registerUserPanel.activationCodeInfo" components={{ 1: <strong /> }} />
                            </div>

                            <div className="kt-center">
                                <Trans
                                    i18nKey="components.registerUserPanel.acceptTerms"
                                    components={{
                                        1: <span className="kt-link kt-underline" onClick={onShowTermsClick} />
                                    }}
                                    values={{
                                        terms: t('components.registerUserPanel.termsShort')
                                    }}
                                />&nbsp;
                                <input name="acceptTerms" type="checkbox" required checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)}></input>
                            </div>

                            <div id="recaptcha-container-register" className="kt-center"></div>

                            <input className={recaptchaSolved ? "kt-btn-big" : "kt-btn-big kt-btn-disabled"} type="submit" value={t("components.buttons.register")} disabled={!recaptchaSolved} />
                        </form>

                        <div className="kt-history-back-link" onClick={e => window.history.back()}><i className="far fa-angle-left"></i> {t("components.buttons.back")}</div>
                    </div>
                </div>

                <Popup visible={termsPopupVisible} onClosePopup={handleCloseTermsPopup}>
                    <TermsPanel />
                </Popup>

            </div>


    );
}